import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSearchParams } from "react-router-dom";
import { db } from "@healthdesk/shared-services";
import { startMapCarouselWalletTopUpCheckout } from "../../services/mapCarousel";
import { contentCardSx } from "../../styles/standaloneLayoutTokens";

const DEFAULT_TOP_UP_USD = 50;

/**
 * Prepaid CareMap carousel wallet (facility or promotional sponsor).
 *
 * @param {{
 *   ownerKind: 'user' | 'facility',
 *   uid: string,
 *   facilityId?: string,
 *   checkoutReturnSegment: 'facility' | 'advertiser' | 'advertiser-budget',
 *   onCheckoutError?: (message: string) => void,
 *   onTopUpReturn?: (kind: 'success' | 'cancel') => void,
 *   balanceRefreshKey?: number | string,
 * }} props
 */
const PrepaidCarouselBudgetCard = ({
  ownerKind,
  uid,
  facilityId = "",
  checkoutReturnSegment,
  onCheckoutError,
  onTopUpReturn,
  balanceRefreshKey = 0,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [walletBalanceCents, setWalletBalanceCents] = useState(null);
  const [topUpAmountUsd, setTopUpAmountUsd] = useState(DEFAULT_TOP_UP_USD);
  const [topUpLoading, setTopUpLoading] = useState(false);

  const walletMainRef = useMemo(() => {
    if (ownerKind === "user" && uid) {
      return db.collection("users").doc(uid).collection("mapCarouselWallet").doc("main");
    }
    if (ownerKind === "facility" && facilityId) {
      return db
        .collection("facilities")
        .doc(facilityId)
        .collection("mapCarouselWallet")
        .doc("main");
    }
    return null;
  }, [ownerKind, uid, facilityId]);

  useEffect(() => {
    const t = searchParams.get("topUp");
    if (t === "success" || t === "cancel") {
      onTopUpReturn?.(t);
      const next = new URLSearchParams(searchParams);
      next.delete("topUp");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, onTopUpReturn]);

  useEffect(() => {
    if (!walletMainRef) {
      setWalletBalanceCents(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const snap = await walletMainRef.get();
        const c = snap.exists ? Number(snap.data()?.balanceCents) : 0;
        if (!cancelled) {
          setWalletBalanceCents(Number.isFinite(c) ? c : 0);
        }
      } catch {
        if (!cancelled) setWalletBalanceCents(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [walletMainRef, balanceRefreshKey]);

  if (!walletMainRef) return null;

  return (
    <Card sx={{ ...contentCardSx, mb: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          Prepaid carousel balance
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {walletBalanceCents == null
            ? "Loading balance…"
            : `About $${(walletBalanceCents / 100).toFixed(2)} USD available for carousel debits.`}{" "}
          You need a minimum prepaid balance (default $1.50) before ads can go live on the map after
          sync or admin approval. Standard slides use CPC (per click); YouTube video slides use CPM
          (per impression when the slide appears in the carousel).
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <TextField
            size="small"
            type="number"
            label="Add funds (USD)"
            value={topUpAmountUsd}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setTopUpAmountUsd(Number.isFinite(v) ? v : DEFAULT_TOP_UP_USD);
            }}
            inputProps={{ min: 5, max: 5000, step: 1 }}
            sx={{ width: 160 }}
          />
          <LoadingButton
            variant="contained"
            loading={topUpLoading}
            onClick={async () => {
              setTopUpLoading(true);
              const r = await startMapCarouselWalletTopUpCheckout({
                amountUsd: topUpAmountUsd,
                ownerKind,
                facilityId: ownerKind === "facility" ? facilityId : undefined,
                returnSegment: checkoutReturnSegment,
              });
              setTopUpLoading(false);
              if (!r.ok) {
                onCheckoutError?.(r.message || "Could not start Stripe checkout.");
              }
            }}
          >
            Add funds (Stripe)
          </LoadingButton>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Secure payment via Stripe Checkout. Credits post after payment succeeds (usually seconds).
          Low-balance email alerts use SMTP if configured on the backend.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PrepaidCarouselBudgetCard;
